import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ExternalLink, Search, Filter, MapPin, Calendar, ChevronRight, Briefcase, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import CaseStagePipeline, { CASE_STAGES } from "@/components/CaseStagePipeline";

const STAGE_FILTER_OPTIONS = ["all", ...CASE_STAGES];

function StageChip({ stage, t }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[#E0E4E2] bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#11322A]">
      {t(`stage.${stage}`)}
    </span>
  );
}

export default function AttorneyDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const t = useT();
  const [me, setMe] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        api.get("/attorney/me"),
        api.get("/attorney/cases", { params: { q: q || undefined, stage } }),
      ]);
      setMe(m.data);
      setCases(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [q, stage]);

  useEffect(() => {
    load();
  }, [load]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const stageCounts = CASE_STAGES.reduce((acc, s) => {
    acc[s] = cases.filter((c) => c.case_stage === s).length;
    return acc;
  }, {});
  const openCount = cases.filter((c) => c.case_stage !== "closed").length;

  return (
    <div className="min-h-screen" data-testid="attorney-dashboard">
      <header className="glass-header sticky top-0 z-40">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div
              className="grid h-9 w-9 place-items-center rounded-lg font-display font-black text-white"
              style={{ background: "var(--brand)" }}
            >
              A
            </div>
            <div className="leading-tight">
              <div className="font-display text-base font-black tracking-tight text-[#11322A]">
                {t("att.portal.title")}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C6B68]">
                {me?.firm_name || (me?.name ? me.name : t("att.portal.subtitle"))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden text-sm font-bold text-[#5C6B68] hover:text-[#11322A] sm:inline-flex items-center gap-1.5">
              <ExternalLink className="h-3.5 w-3.5" /> {t("att.portal.view_site")}
            </Link>
            <div className="hidden text-right text-xs sm:block">
              <div className="font-bold text-[#0B1317]">{user?.name}</div>
              <div className="text-[#5C6B68]">{user?.email}</div>
            </div>
            <button
              onClick={doLogout}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#E0E4E2] bg-white px-3 py-2 text-sm font-bold text-[#0B1317] hover:bg-[#FDFBF7]"
              data-testid="att-logout"
            >
              <LogOut className="h-3.5 w-3.5" /> {t("mc.logout")}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6">
        {/* Stat cards */}
        <div className="grid gap-4 sm:grid-cols-4" data-testid="att-stats">
          <div className="card-soft p-5">
            <div className="label-cap text-[10px]">{t("att.stats.open")}</div>
            <div className="mt-2 font-display text-3xl font-black text-[#11322A]">{openCount}</div>
          </div>
          <div className="card-soft p-5">
            <div className="label-cap text-[10px]">{t("att.stats.total")}</div>
            <div className="mt-2 font-display text-3xl font-black text-[#11322A]">{cases.length}</div>
          </div>
          <div className="card-soft p-5">
            <div className="label-cap text-[10px]">{t("att.stats.treatment")}</div>
            <div className="mt-2 font-display text-3xl font-black text-[#E85D04]">
              {stageCounts.treatment || 0}
            </div>
          </div>
          <div className="card-soft p-5">
            <div className="label-cap text-[10px]">{t("att.stats.settlement")}</div>
            <div className="mt-2 font-display text-3xl font-black text-[#166534]">
              {stageCounts.settlement || 0}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card-soft mt-6 overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 border-b border-[#E0E4E2] bg-[#FDFBF7] p-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5C6B68]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("att.filter.search_placeholder")}
                className="input-field pl-9"
                data-testid="att-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-[#5C6B68]" />
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="input-field !w-auto"
                data-testid="att-stage-filter"
              >
                {STAGE_FILTER_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s === "all" ? t("att.filter.all_stages") : t(`stage.${s}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="p-12 text-center text-[#5C6B68]">{t("mc.loading")}</div>
          ) : cases.length === 0 ? (
            <div className="p-12 text-center" data-testid="att-empty">
              <Briefcase className="mx-auto h-8 w-8 text-[#5C6B68]" />
              <h2 className="mt-4 font-display text-xl font-bold text-[#0B1317]">
                {t("att.empty.title")}
              </h2>
              <p className="mt-2 text-sm text-[#5C6B68]">{t("att.empty.body")}</p>
            </div>
          ) : (
            <ul className="divide-y divide-[#E0E4E2]" data-testid="att-cases-list">
              {cases.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => navigate(`/attorney/cases/${c.id}`)}
                    className="grid w-full grid-cols-1 items-center gap-3 px-4 py-4 text-left transition hover:bg-[#FDFBF7] sm:grid-cols-12"
                    data-testid={`att-case-${c.id}`}
                  >
                    <div className="sm:col-span-4">
                      <div className="font-display text-base font-bold text-[#11322A]">
                        {c.full_name}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#5C6B68]">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {c.state}
                          {c.city ? ` · ${c.city}` : ""}
                        </span>
                        {c.accident_date && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {c.accident_date}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="sm:col-span-3 text-xs text-[#0B1317]">
                      {c.accident_type || t("mc.fallback_title")}
                    </div>
                    <div className="sm:col-span-3">
                      <StageChip stage={c.case_stage || "intake"} t={t} />
                    </div>
                    <div className="hidden sm:col-span-2 sm:flex items-center justify-end gap-2 text-xs font-semibold text-[#5C6B68]">
                      {c.attorney_assigned_method === "auto_city" && (
                        <span className="inline-flex items-center gap-1 text-[#166534]">
                          <ShieldCheck className="h-3 w-3" /> {t("att.auto_assigned")}
                        </span>
                      )}
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
